import { createRouter, createWebHistory } from 'vue-router';

import { store } from '@/store';
import ability from '@/plugins/abilities';
import { useUserHasAccessTo } from '@/Core.UI.Domain/Composables/userHasAccessTo';
import {
  isChunkLoadError,
  reloadForNewChunks,
  clearChunkReloadGuard,
} from '@/utils/chunk-reload';

import Callback from '@/components/Callback.vue';
import Logout from '@/components/Logout.vue';

// Core Service Domain

// Controls Domain
import ControlsRoutes from '@/Core.Service.Domain/Controls/Controls.Common/Routes';
// Strategies Domain
import StrategiesRoutes from '@/Core.Service.Domain/Strategies/Strategies.Common/Routes';
// Analytics Domain
import AnalyticsRoutes from '@/Core.Service.Domain/Analytics/Analytics.Common/Routes';
// Sites Domain
import SitesRoutes from '@/Core.Service.Domain/Sites/Sites.Common/Routes';
// View Support Domain
import PortfolioRoutes from '@/Core.Service.Domain/ViewSupport/Portfolio/Routes';
import ReportsRoutes from '@/Core.Service.Domain/ViewSupport/Reports/Routes';
import KioskRoutes from '@/Core.Service.Domain/ViewSupport/Kiosk/Routes';
import NotificationsRoutes from '@/Core.Service.Domain/ViewSupport/Notifications/Routes';
// Contact Domain
import ContactRoutes from '@/Core.Service.Domain/Contact/Routes';

// Admin Domain
import AdminRoutes from '@/Core.Service.Domain/Admin/Routes';

// Users Domain
// These routes are old fashioned and should be refactored to be part of the new Users Domain.
import UsersRoutes from '@/Core.Service.Domain/Users/Routes';

// These routes are old fashioned and should be refactored to be part of the new Users Domain.
import PermissionsRoutes from '@/Core.Service.Domain/Permissions/Routes';

// Monitors Domain (Egauges)
// These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
import EgaugesRoutes from '@/Core.Service.Domain/Monitors/Egauges/Routes';

// Monitors Domain (Utility Meters)
// These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
import UtilityMetersRoutes from '@/Core.Service.Domain/Monitors/UtilityMeters/Routes';

// Monitors Domain (Manual Utility Monitors)
// These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
import ManualUtilityMonitorsRoutes from '@/Core.Service.Domain/Monitors/ManualUtilityMonitors/Routes';

// Monitors Domain (Virtual Monitors)
// These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
import VirtualMonitorsRoutes from '@/Core.Service.Domain/Monitors/VirtualMonitors/Routes';

import DevicesRoutes from '@/Core.Service.Domain/Devices/Devices.Common/Routes/index.ts';

let routes = [];

const HomeRoute = {
  path: '/',
  name: 'Home',
  redirect: () => {
    return { name: 'Portfolio' };
  },
};

const CallbackRoute = {
  path: '/callback',
  name: 'Callback',
  component: Callback,
  meta: { allowAnonymous: true },
};

const LogoutRoute = {
  path: '/logout',
  name: 'Logout',
  component: Logout,
  meta: { allowAnonymous: true },
};

routes.push(HomeRoute, CallbackRoute, LogoutRoute);

const ApplicationErrorRoute = {
  path: '/error',
  name: 'ApplicationError',
  component: () => import('@/modules/errors/_components/Index'),
  meta: { allowAnonymous: true },
};
routes.push(ApplicationErrorRoute);

/**********************************
 * Site Routes
 */
SitesRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Heat Map Routes
 */
// const HeatMapIndexRoute = {
//   path: '/heatmaps',
//   name: 'HeatMapIndex',
//   component: () => import('@/modules/heatmaps/_components/Index'),
// }

// routes.push(HeatMapIndexRoute)

/***************************************
 * Analytics Routes
 */
AnalyticsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Control Routes
 */
ControlsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Portfolio Routes
 */
PortfolioRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Notifications Routes
 */
NotificationsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Report Routes
 */
ReportsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Admin Routes
 */
AdminRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * User Routes
 * These routes are old fashioned and should be refactored to be part of the new Users Domain.
 */
UsersRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * User Routes
 * These routes are old fashioned and should be refactored to be part of the new Permission Domain.
 */
PermissionsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * SubMetering Routes(Egauges)
 * These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
 */
EgaugesRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * SubMetering Routes(Utility Meters)
 * These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
 */
UtilityMetersRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Monitor Routes(Manual Utility Monitors)
 * These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
 */
ManualUtilityMonitorsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Monitor Routes(Virtual Monitors)
 * These routes are old fashioned and should be refactored to be part of the new Monitors Domain.
 */
VirtualMonitorsRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Kiosk Routes
 */
KioskRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Contact Routes
 */
ContactRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Strategies Routes
 */
StrategiesRoutes.forEach((route) => {
  routes.push(route);
});

/***************************************
 * Devices Routes
 */
DevicesRoutes.forEach((route) => {
  routes.push(route);
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0, behavior: 'smooth' };
  },
});

// A failed lazy route-component import after a deploy means the client is
// running a stale build referencing chunks that no longer exist. Reload once
// to pick up the freshly deployed build instead of leaving a dead screen.
router.onError((error) => {
  if (isChunkLoadError(error)) {
    reloadForNewChunks();
  }
});

// Once navigation succeeds the app is on the current build; allow recovery
// again on a future deploy within the same session.
router.afterEach(() => {
  clearChunkReloadGuard();
});

router.beforeEach(async (to, from) => {
  const allowAnonymous = to.matched.some((route) => route.meta.allowAnonymous);
  const isAuthenticated = store.getters['session/isAuthenticated'];
  const userPermissionsSet = store.getters['session/userPermissionsSet'];

  // Early redirect for unauthenticated users trying to access protected routes
  if (!allowAnonymous && !isAuthenticated) {
    // Save the intended destination
    if (to.fullPath !== '/logout') {
      localStorage.setItem('redirect', to.fullPath);
    }
    // Trigger login immediately - this will redirect to Auth0
    store.dispatch('session/login');
    // Stop navigation to prevent rendering
    return false;
  }

  // Ensure user permissions are initialized if authenticated
  if (isAuthenticated && !userPermissionsSet) {
    console.log(
      'User permissions not loaded yet, initializing session in router'
    );
    try {
      await store.dispatch('session/initializeSession');
    } catch (error) {
      console.error('Error initializing session:', error);
      return {
        name: 'ApplicationError',
        query: { message: 'Failed to initialize session' },
      };
    }
  }

  // Check permissions for authenticated users
  if (to.meta.permission) {
    const [subject, action] = to.meta.permission.split(':');
    const noAccess = ability.cannot(action, subject);

    if (noAccess) {
      return {
        name: 'ApplicationError',
        query: { redirectFrom: to.fullPath, originalUrl: from.fullPath },
      };
    }
  }

  // Some routes (e.g. Controls types) are hidden from the lateral menu when
  // no site in the user's portfolio has that capability. Deny deep links the
  // same way, otherwise the view renders and loads forever.
  if (isAuthenticated && to.meta.requiresSiteProperty) {
    if (
      !store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesLoaded']
    ) {
      await store.dispatch('ViewSupportStore/PortfolioStore/getPortfolioSites');

      // Still not loaded means the fetch failed; surface that instead of
      // wrongly telling the user they lack access. The flag stays false, so
      // the next navigation retries the fetch.
      if (
        !store.getters[
          'ViewSupportStore/PortfolioStore/getPortfolioSitesLoaded'
        ]
      ) {
        return {
          name: 'ApplicationError',
          query: { message: 'Failed to load portfolio sites' },
        };
      }
    }

    if (!useUserHasAccessTo(to.meta.requiresSiteProperty, store)) {
      return {
        name: 'ApplicationError',
        query: { redirectFrom: to.fullPath, originalUrl: from.fullPath },
      };
    }
  }

  // Validate defaultSite against user's portfolio on every route change
  if (isAuthenticated) {
    const defaultSite = store.getters['session/defaultSite'];
    const portfolioSites =
      store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesList'] ||
      [];

    // Only validate if both defaultSite and portfolioSites are available
    if (defaultSite && portfolioSites.length > 0) {
      const siteId = defaultSite.siteId || defaultSite.id;
      const isAuthorized = portfolioSites.some(
        (portfolioSite) => (portfolioSite.siteId || portfolioSite.id) === siteId
      );

      // If the defaultSite is not authorized, reset it to the first authorized site
      if (!isAuthorized) {
        store.dispatch('session/setDefaultSite', portfolioSites[0]);
      }
    }
  }

  // Allow navigation
  return true;
});

export default router;
